// -- REST endpoints -------------------------------------------------------

export const ORG_ROLES_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/roles`;

export const ORG_ROLE_ENDPOINT = (organizationId: string, roleId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/roles/${encodeURIComponent(roleId)}`;

export const ORG_SERVICES_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/services`;

export const ORG_MEMBERS_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/members`;

/*
 * `ORG_MEMBER_ENDPOINT` (`PATCH /orgs/:organizationId/members/:memberId`) is gone.
 *
 * It was the role-reassignment call. The backend retired it in favour of the `updateOrgUserRole`
 * mutation below, so the builder went with it — leaving it would leave one `axiosInstance.patch`
 * away from a 404 that looks like a permissions problem.
 */

export const ORG_INVITATIONS_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/invitations`;

export const ORG_INVITATION_ENDPOINT = (organizationId: string, invitationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}`;

export const ORG_INVITATION_RESEND_ENDPOINT = (organizationId: string, invitationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}/resend`;

export const ORG_USAGE_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage`;

export const ORG_USAGE_REPORT_ENDPOINT = (organizationId: string, days: number) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/report?days=${days}`;

// Batched sibling of ORG_USAGE_REPORT_ENDPOINT above (plural `/reports`, distinct route) — one
// per-member LiteLLM fetch on the backend instead of one per requested `days` value. Use this
// instead of calling ORG_USAGE_REPORT_ENDPOINT once per window in a loop: firing N single-window
// requests concurrently multiplies load on the shared litellm proxy for data one fetch already
// covers (measured pushing each request from ~5s to 16-19s — see ncoder_backend's
// docs/invariants/llm-cost-governance.md, 2026-09-02 entry).
export const ORG_USAGE_REPORTS_BATCH_ENDPOINT = (organizationId: string, days: readonly number[]) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/reports?${days
    .map((d) => `days[]=${d}`)
    .join('&')}`;

// Additive sibling of ORG_USAGE_REPORT_ENDPOINT above — `?month=` is mutually exclusive with
// `?days=` on the backend, so this is a separate query param, not a value ORG_USAGE_REPORT_ENDPOINT
// could also send.
export const ORG_USAGE_REPORT_MONTH_ENDPOINT = (organizationId: string, month: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/report?month=${month}`;

// Observability-backed (`observability_events`) — distinct data source from
// ORG_USAGE_REPORT_ENDPOINT above, same REST namespace.
export const ORG_USAGE_SERVICE_REPORT_ENDPOINT = (organizationId: string, days: number) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/service_report?days=${days}`;

export const ORG_USAGE_SERVICE_REPORT_MONTH_ENDPOINT = (organizationId: string, month: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/service_report?month=${month}`;

// Per-member version of the same observability data.
export const ORG_USAGE_SERVICE_REPORT_USERS_ENDPOINT = (organizationId: string, days: number) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/service_report/users?days=${days}`;

export const ORG_USAGE_SERVICE_REPORT_USERS_MONTH_ENDPOINT = (organizationId: string, month: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/service_report/users?month=${month}`;

// The only org-admin usage endpoint with a TIME dimension: one row per calendar
// day rather than one aggregate over the window. Every other endpoint in this
// namespace widens with `days` instead of bucketing by it, which is why the
// Analysis trend had to be faked until this landed.
export const ORG_USAGE_TIMESERIES_ENDPOINT = (organizationId: string, days: number) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/timeseries?days=${days}`;

export const ORG_USAGE_TIMESERIES_MONTH_ENDPOINT = (organizationId: string, month: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/timeseries?month=${month}`;

export const ORG_MEMBER_USAGE_ENDPOINT = (organizationId: string, userId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/members/${encodeURIComponent(userId)}`;

export const ORG_MEMBER_USAGE_BUDGET_ENDPOINT = (organizationId: string, userId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/usage/members/${encodeURIComponent(userId)}/budget`;

// -- GraphQL — member lifecycle and role assignment ----
//
// THIS NOTE USED TO SAY THE OPPOSITE, and the reversal is worth recording. Role reassignment lived
// on REST (`PATCH .../members/:memberId`) and the GraphQL equivalent had been retired as redundant.
// The backend has since retired the REST route instead, so all three member mutations — assign role,
// deactivate, reactivate — now go through GraphQL. If a future reader finds a REST members PATCH in
// the backend again, this is the history: it existed, it was removed, do not assume it is the newer
// of the two.

/**
 * Assign a role to a member.
 *
 * `organizationId`, `userId` and `roleId` are all required. The target must be an ACTIVE member of
 * the org and the role must belong to it — both enforced server-side, which is why the callers gate
 * the control rather than filtering the error after the fact.
 *
 * Note what is NOT in the input: there is no owner/admin/member field. Authority follows the role's
 * own `management_level`, so `role` comes back in the response and is never sent. Selecting it is
 * how the UI learns whether the assignment escalated or demoted someone.
 */
export const UPDATE_ORG_USER_ROLE_MUTATION = `
  mutation UpdateOrgUserRole($input: UpdateOrgUserRoleInput!) {
    updateOrgUserRole(input: $input) {
      memberId
      organizationId
      userId
      role
      roleId
      roleName
      status
    }
  }
`;


export const DEACTIVATE_ORG_USER_MUTATION = `
  mutation DeactivateOrgUser($input: OrgUserIdInput!) {
    deactivateOrgUser(input: $input) {
      memberId
      organizationId
      userId
      role
      status
      deactivatedAt
      deactivatedByUserId
    }
  }
`;

export const REACTIVATE_ORG_USER_MUTATION = `
  mutation ReactivateOrgUser($input: OrgUserIdInput!) {
    reactivateOrgUser(input: $input) {
      memberId
      organizationId
      userId
      role
      status
      deactivatedAt
    }
  }
`;

// -- Billing (Stripe-backed) ------------------------------------------------
// See docs/billing-api.md. All five require owner/admin membership; the Stripe
// webhook (`POST /webhooks/stripe`) is backend-only and is never called here.

export const ORG_BILLING_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing`;

export const ORG_BILLING_CHECKOUT_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/checkout`;

export const ORG_BILLING_TOPUP_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/topup`;

export const ORG_BILLING_TOPUP_BULK_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/topup/bulk`;

export const ORG_BILLING_CREDITS_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/credits`;

export const ORG_BILLING_CREDITS_PURCHASE_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/credits/purchase`;

export const ORG_BILLING_CREDITS_ASSIGN_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/credits/assign`;

export const ORG_BILLING_PORTAL_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/portal`;

export const ORG_BILLING_CANCEL_ENDPOINT = (organizationId: string, userId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/subscriptions/${encodeURIComponent(userId)}/cancel`;

/** Undoes a scheduled cancellation, so the subscription renews at period end. */
export const ORG_BILLING_RESUME_ENDPOINT = (organizationId: string, userId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/subscriptions/${encodeURIComponent(userId)}/resume`;

// PUT, not POST: sending the same target plan twice must not stack two prorations. The backend
// rejects a no-op change with 409 `already_on_plan`.
export const ORG_BILLING_CHANGE_PLAN_ENDPOINT = (organizationId: string, userId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/subscriptions/${encodeURIComponent(userId)}/plan`;

export const ORG_BILLING_INVOICES_ENDPOINT = (organizationId: string) =>
  `/orgs/${encodeURIComponent(organizationId)}/billing/invoices`;
